import React, { useState, useEffect, useCallback } from 'react';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  Area,
  AreaChart
} from 'recharts';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Tabs,
  Tab,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Pagination,
  styled
} from '@mui/material';
import {
  TrendingUp,
  Security,
  Assessment,
  Schedule,
  CheckCircle,
  Info,
  BarChart,
  Timeline,
  Close as CloseIcon,
  DonutLarge,
  History as HistoryIcon,
  ShowChart,
  VerifiedUser,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import {
  getCurrentUserTrialSummary,
  TrialUserSummary,
  TrialActivity,
  EnhancedTrialActivity,
  UsageSummaryResponse,
  checkCurrentUserTrialStatus,
  getTrialActivities,
  getRecentActivities,
  getUsageSummary
} from '../../services/trialActivityApi';
import { User } from '../../types/auth';
import { error, log } from '../../utils/logger';
import { isSubscribedUser, isTrialUser } from 'utils/trialUtils';

interface TrialStatsDialogProps {
  open: boolean;
  onClose: () => void;
  user: User | null;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`trial-tabpanel-${index}`}
      aria-labelledby={`trial-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    width: '80%',
    maxWidth: '70vw',
    maxHeight: '90vh',
    background: theme.palette.mode === 'dark' ? 'rgba(17, 17, 17, 0.98)' : '#FFFFFF',
    borderRadius: '12px',
    padding: '20px 20px',
    boxShadow: theme.palette.mode === 'dark' ? '0 4px 20px rgba(0, 0, 0, 0.5)' : '0 4px 20px rgba(0, 0, 0, 0.15)',
    display: 'flex',
    flexDirection: 'column',
    border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)'}`,
  },
  '& .MuiDialogTitle-root': {
    padding: 0,
    paddingBottom: '16px',
    fontSize: '24px',
    fontWeight: 500,
    color: theme.palette.mode === 'dark' ? '#F1F5F9' : '#1E2533',
    position: 'relative',
    flexShrink: 0,
  },
  '& .MuiDialogContent-root': {
    padding: '0',
    overflowY: 'auto',
    '&::-webkit-scrollbar': {
      width: '8px',
    },
    '&::-webkit-scrollbar-track': {
      background: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
    },
    '&::-webkit-scrollbar-thumb': {
      background: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
      borderRadius: '4px',
    },
    '&::-webkit-scrollbar-thumb:hover': {
      background: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)',
    },
  },
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  right: '0px',
  top: '0px',
  color: theme.palette.mode === 'dark' ? '#F1F5F9' : '#1E2533',
  padding: '4px',
  '&:hover': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.04)',
  },
  width: '24px',
  height: '24px',
  zIndex: 3,
  '& .MuiSvgIcon-root': {
    fontSize: '20px',
  },
}));

const TrialStatsDialog: React.FC<TrialStatsDialogProps> = ({ open, onClose, user }) => {
  const theme = useTheme();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [trialSummary, setTrialSummary] = useState<TrialUserSummary | null>(null);
  const [recentActivities, setRecentActivities] = useState<TrialActivity[]>([]);
  const [enhancedActivities, setEnhancedActivities] = useState<EnhancedTrialActivity[]>([]);
  const [usageSummary, setUsageSummary] = useState<UsageSummaryResponse | null>(null);
  const [usageDays, setUsageDays] = useState(7);
  const [activitiesPage, setActivitiesPage] = useState(1);
  const [activitiesMeta, setActivitiesMeta] = useState<{
    page: number;
    per_page: number;
    total: number;
    pages: number;
  } | null>(null);

  const checkTrialStatus = useCallback(async () => {
    try {
      const response = await checkCurrentUserTrialStatus();
      log('Trial check response:', response);
    } catch (err: any) {
      error('Error checking trial status:', err);
    } finally {
    }
  }, []);

  const fetchTrialData = useCallback(async (page: number = 1) => {
    setLoading(true);
    try {
      // Get user data from localStorage to get user_id
      const userData = user?.user_id ? { user_id: user.user_id } : {};

      // Fetch trial summary and recent activities
      const [summary, activities, enhancedActivities] = await Promise.all([
        getCurrentUserTrialSummary(),
        getTrialActivities({
          ...userData,
          skip: (page - 1) * 10,
          limit: 10,
          app_type: 'security'
        }),
        getRecentActivities({
          ...userData,
          limit: 10,
          app_type: 'security'
        })
      ]);

      setTrialSummary(summary);
      setRecentActivities(activities.data || []);
      setActivitiesMeta(activities.meta);
      setEnhancedActivities(enhancedActivities.data || []);

      // Call the check and integrate APIs
      await checkTrialStatus();
      // await integrateTrialHeaders();

    } catch (err: any) {
      error('Error fetching trial data:', err);
    } finally {
      setLoading(false);
    }
  }, [user, checkTrialStatus]);

  const fetchUsageSummary = useCallback(async () => {
    try {
      // Get user data from localStorage to get user_id
      const userData = user?.user_id ? { user_id: user.user_id } : {};

      const summary = await getUsageSummary({
        ...userData,
        days: usageDays
      });
      setUsageSummary(summary);
    } catch (err: any) {
      error('Error fetching usage summary:', err);
    }
  }, [user, usageDays]);

  // Reset tab position when dialog is closed
  useEffect(() => {
    if (!open) {
      setTabValue(0);
    }
  }, [open]);

  useEffect(() => {
    if (open && user) {
      fetchTrialData(activitiesPage);
    }
  }, [open, user, activitiesPage, fetchTrialData]);

  useEffect(() => {
    if (open && user) {
      fetchUsageSummary();
    }
  }, [open, user, fetchUsageSummary]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleActivitiesPageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setActivitiesPage(value);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTrialStatusColor = (status: string | undefined) => {
    if (!status) return 'default';

    switch (status.toLowerCase()) {
      case 'active':
        return 'success';
      case 'expired':
        return 'error';
      case 'unlimited':
        return 'info';
      default:
        return 'default';
    }
  };

  const getActivityIcon = (activityType: string) => {
    switch (activityType.toLowerCase()) {
      case 'scan':
        return <Security fontSize="small" />;
      case 'fix':
        return <CheckCircle fontSize="small" />;
      case 'analysis':
        return <Assessment fontSize="small" />;
      default:
        return <Info fontSize="small" />;
    }
  };

  if (!user) return null;

  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              bgcolor: 'primary.main',
              width: 40,
              height: 40,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15)'
            }}
          >
            <TrendingUp sx={{ color: 'white' }} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>Security Usage Statistics</Typography>
        </Box>
        <CloseButton
          onClick={onClose}
          aria-label="close"
        >
          <CloseIcon />
        </CloseButton>
      </DialogTitle>

      <Box sx={{
        position: 'sticky',
        top: 0,
        zIndex: 10,
        bgcolor: theme.palette.mode === 'dark' ? 'rgba(17, 17, 17, 0.95)' : 'rgba(248, 250, 252, 0.95)',
        borderBottom: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
        backdropFilter: 'blur(8px)',
        borderRadius: '8px 8px 0 0',
        mt: 1
      }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            '& .MuiTabs-indicator': {
              backgroundColor: 'primary.main',
              height: 3,
              borderRadius: '3px 3px 0 0'
            },
            '& .MuiTab-root': {
              minHeight: '48px',
              fontWeight: 500,
              fontSize: '0.875rem',
              textTransform: 'none',
              '&.Mui-selected': {
                color: 'primary.main',
                fontWeight: 600
              }
            }
          }}
        >
          <Tab icon={<DonutLarge fontSize="small" />} iconPosition="start" label="Usage Statistics" />
          <Tab icon={<HistoryIcon fontSize="small" />} iconPosition="start" label="Recent Activity" />
          <Tab icon={<Assessment fontSize="small" />} iconPosition="start" label="Usage Stats" />
          <Tab icon={<ShowChart fontSize="small" />} iconPosition="start" label="Usage Trends" />
        </Tabs>
      </Box>

      <DialogContent sx={{ flexGrow: 1 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>

            <TabPanel value={tabValue} index={0}>
              {/* Overview Tab - Simplified to show only the specified statistics */}
              {trialSummary ? (
                <Box>
                  <Typography variant="h6" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Assessment fontSize="small" />
                    Security Usage Statistics
                  </Typography>

                  <Grid container spacing={3}>
                    {/* Total Activities */}
                    <Grid item xs={12} sm={6} md={3}>
                      <Card
                        sx={{
                          height: '100%',
                          borderRadius: 3,
                          background: theme.palette.mode === 'dark'
                            ? theme.palette.primary.dark + '15'
                            : theme.palette.primary.light + '10',
                          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                          transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                          border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(66, 165, 245, 0.2)' : 'rgba(25, 118, 210, 0.1)'}`,
                          '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
                          }
                        }}
                      >
                        <CardContent sx={{ p: 2 }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 60,
                                height: 60,
                                borderRadius: '50%',
                                mb: 2,
                                background: theme.palette.primary.main,
                                boxShadow: `0 4px 8px ${theme.palette.primary.main}40`,
                              }}
                            >
                              <TrendingUp sx={{ color: 'white' }} fontSize="large" />
                            </Box>
                            <Typography variant="h4" color="primary" fontWeight="bold">
                              {trialSummary.total_activities}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                              Total Activities
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>

                    {/* Total Scans */}
                    <Grid item xs={12} sm={6} md={3}>
                      <Card
                        sx={{
                          height: '100%',
                          borderRadius: 3,
                          background: theme.palette.mode === 'dark'
                            ? theme.palette.success.dark + '15'
                            : theme.palette.success.light + '10',
                          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                          transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                          border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(129, 199, 132, 0.2)' : 'rgba(56, 142, 60, 0.1)'}`,
                          '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
                          }
                        }}
                      >
                        <CardContent sx={{ p: 2 }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 60,
                                height: 60,
                                borderRadius: '50%',
                                mb: 2,
                                background: theme.palette.success.main,
                                boxShadow: `0 4px 8px ${theme.palette.success.main}40`,
                              }}
                            >
                              <Security sx={{ color: 'white' }} fontSize="large" />
                            </Box>
                            <Typography variant="h4" color="success.main" fontWeight="bold">
                              {trialSummary.total_scans}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                              Total Scans
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>

                    {/* Total Fixes */}
                    <Grid item xs={12} sm={6} md={3}>
                      <Card
                        sx={{
                          height: '100%',
                          borderRadius: 3,
                          background: theme.palette.mode === 'dark'
                            ? theme.palette.warning.dark + '15'
                            : theme.palette.warning.light + '10',
                          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                          transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                          border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 183, 77, 0.2)' : 'rgba(237, 108, 2, 0.1)'}`,
                          '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
                          }
                        }}
                      >
                        <CardContent sx={{ p: 2 }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 60,
                                height: 60,
                                borderRadius: '50%',
                                mb: 2,
                                background: theme.palette.warning.main,
                                boxShadow: `0 4px 8px ${theme.palette.warning.main}40`,
                              }}
                            >
                              <CheckCircle sx={{ color: 'white' }} fontSize="large" />
                            </Box>
                            <Typography variant="h4" color="warning.main" fontWeight="bold">
                              {trialSummary.total_fixes}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                              Total Fixes
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>

                    {/* Total Credits Used */}
                    <Grid item xs={12} sm={6} md={3}>
                      <Card
                        sx={{
                          height: '100%',
                          borderRadius: 3,
                          background: theme.palette.mode === 'dark'
                            ? theme.palette.error.dark + '15'
                            : theme.palette.error.light + '10',
                          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                          transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                          border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(229, 115, 115, 0.2)' : 'rgba(211, 47, 47, 0.1)'}`,
                          '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
                          }
                        }}
                      >
                        <CardContent sx={{ p: 2 }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 60,
                                height: 60,
                                borderRadius: '50%',
                                mb: 2,
                                background: theme.palette.error.main,
                                boxShadow: `0 4px 8px ${theme.palette.error.main}40`,
                              }}
                            >
                              <BarChart sx={{ color: 'white' }} fontSize="large" />
                            </Box>
                            <Typography variant="h4" color="error.main" fontWeight="bold">
                              {trialSummary.total_credits_used.toFixed(1)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                              Credits Used
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="h6" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <VerifiedUser fontSize="small" />
                      User Summary
                    </Typography>
                    <Grid container spacing={3}>
                      {/* User Summary Section */}
                      <Grid item xs={12}>
                        <Card>
                          <CardContent>
                            {trialSummary ? (
                              <Box>
                                <Grid container spacing={2}>
                                  <Grid item xs={12} md={12}>
                                    <Card variant="outlined">
                                      <CardContent>
                                        <Typography variant="subtitle2" gutterBottom>
                                          User Information
                                        </Typography>
                                        <Box sx={{ mt: 1 }}>
                                          <Typography variant="body2" color="text.secondary">
                                            <strong>User ID:</strong> {trialSummary.user_id}
                                          </Typography>
                                          <Typography variant="body2" color="text.secondary">
                                            <strong>Email:</strong> {trialSummary.user_email}
                                          </Typography>
                                          <Typography variant="body2" color="text.secondary">
                                            <strong>Name:</strong> {trialSummary.user_name}
                                          </Typography>
                                          {trialSummary.organization_name && (
                                            <Typography variant="body2" color="text.secondary">
                                              <strong>Organization:</strong> {trialSummary.organization_name}
                                            </Typography>
                                          )}
                                        </Box>
                                      </CardContent>
                                    </Card>
                                  </Grid>

                                  {/* Only show trial status for non-subscribed users */}
                                  {isTrialUser() && (
                                    <Grid item xs={12} md={12}>
                                      <Card variant="outlined">
                                        <CardContent>
                                          <Typography variant="subtitle2" gutterBottom>
                                            Trial Status
                                          </Typography>
                                          <Box sx={{ mt: 1 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                              <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                                                <strong>Status:</strong>
                                              </Typography>
                                              <Chip
                                                label={trialSummary.current_trial_status}
                                                color={getTrialStatusColor(trialSummary.current_trial_status)}
                                                size="small"
                                              />
                                            </Box>
                                            <Typography variant="body2" color="text.secondary">
                                              <strong>Expires:</strong> {trialSummary.trial_expires ? formatDate(trialSummary.trial_expires) : 'N/A'}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                              <strong>Days Remaining:</strong> {trialSummary.days_remaining !== undefined ? trialSummary.days_remaining : 'N/A'}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                              <strong>Last Activity:</strong> {trialSummary.last_activity ? formatDate(trialSummary.last_activity) : 'N/A'}
                                            </Typography>
                                          </Box>
                                        </CardContent>
                                      </Card>
                                    </Grid>
                                  )}

                                  {/* For subscribed users, show subscription status instead */}
                                  {isSubscribedUser() && (
                                    <Grid item xs={12} md={12}>
                                      <Card variant="outlined">
                                        <CardContent>
                                          <Typography variant="subtitle2" gutterBottom>
                                            Subscription Status
                                          </Typography>
                                          <Box sx={{ mt: 1 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                              <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                                                <strong>Type:</strong>
                                              </Typography>
                                              <Chip
                                                label="Subscribed"
                                                color="success"
                                                size="small"
                                              />
                                            </Box>
                                            <Typography variant="body2" color="text.secondary">
                                              <strong>Status:</strong> {user?.subscription_status || 'Active'}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                              <strong>Last Activity:</strong> {trialSummary.last_activity ? formatDate(trialSummary.last_activity) : 'N/A'}
                                            </Typography>
                                          </Box>
                                        </CardContent>
                                      </Card>
                                    </Grid>
                                  )}
                                </Grid>
                              </Box>
                            ) : (
                              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                                <CircularProgress size={24} />
                              </Box>
                            )}
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>
                  </Box>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              )}
            </TabPanel>

            <TabPanel value={tabValue} index={1}>
              {/* Recent Activity Tab */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <HistoryIcon color="primary" />
                  <Typography variant="h6">
                    Recent Security Activities
                  </Typography>
                </Box>
                <Chip
                  label={`${recentActivities.length} Activities`}
                  color="primary"
                  variant="outlined"
                  size="small"
                  sx={{ fontWeight: 500 }}
                />
              </Box>

              {recentActivities.length > 0 ? (
                <>
                  <Grid container spacing={2}>
                    {recentActivities.map((activity, index) => {
                      // Determine activity color based on type
                      const getActivityColor = (type: string) => {
                        switch (type.toLowerCase()) {
                          case 'scan': return 'primary';
                          case 'fix': return 'success';
                          case 'analysis': return 'warning';
                          default: return 'info';
                        }
                      };

                      const activityColor = getActivityColor(activity.activity_type);

                      return (
                        <Grid item xs={12} key={activity.id}>
                          <Card
                            sx={{
                              borderRadius: 1,
                              border: `1px solid ${theme.palette.mode === 'dark'
                                ? `rgba(255, 255, 255, 0.1)`
                                : `rgba(0, 0, 0, 0.05)`}`,
                              background: theme.palette.mode === 'dark'
                                ? theme.palette[activityColor].dark + '15'
                                : theme.palette[activityColor].light + '10',
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: `0 8px 16px ${theme.palette[activityColor].main}20`,
                                borderColor: theme.palette[activityColor].main + '40',
                              },
                              position: 'relative',
                              overflow: 'visible'
                            }}
                          >
                            {/* Activity Timeline Indicator */}
                            {index < recentActivities.length - 1 && (
                              <Box sx={{
                                position: 'absolute',
                                left: 28,
                                top: '100%',
                                height: 16,
                                width: 2,
                                bgcolor: theme.palette.divider,
                                zIndex: 1
                              }} />
                            )}

                            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                              <Grid container spacing={1} alignItems="center">
                                {/* Left Column - Icon and Timeline */}
                                <Grid item xs={6} sm={1} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      width: 38,
                                      height: 38,
                                      borderRadius: '50%',
                                      bgcolor: `${activityColor}.main`,
                                      boxShadow: `0 4px 8px ${theme.palette[activityColor].main}40`,
                                      zIndex: 2,
                                      border: `2px solid ${theme.palette.background.paper}`
                                    }}
                                  >
                                    {getActivityIcon(activity.activity_type)}
                                  </Box>
                                </Grid>

                                {/* Right Column - Activity Details */}
                                <Grid item xs={12} sm={11}>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%' }}>
                                    {/* Left side - Activity details */}
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                      {/* Activity Title and Status */}
                                      <Box sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        mb: 1,
                                        flexWrap: 'wrap'
                                      }}>
                                        <Typography variant="subtitle1" fontWeight="600" noWrap>
                                          {activity.request_details?.scan_type?.replace(/_/g, ' ').toUpperCase() || ''} {activity.activity_type.charAt(0).toUpperCase() + activity.activity_type.slice(1)}
                                          {activity.app_type && ` - ${activity.app_type}`}
                                        </Typography>

                                        {/* <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                        <Chip
                                          label={activity.trial_status}
                                          size="small"
                                          color={getTrialStatusColor(activity.trial_status) as any}
                                          sx={{
                                            height: 20,
                                            '& .MuiChip-label': { px: 1, py: 0.5, fontSize: '0.7rem' },
                                            fontWeight: 500
                                          }}
                                        />
                                        
                                        {activity.response_details?.status && (
                                          <Chip
                                            label={activity.response_details.status}
                                            size="small"
                                            color={
                                              activity.response_details.status.toLowerCase() === 'completed' ? 'success' :
                                              activity.response_details.status.toLowerCase() === 'failed' ? 'error' :
                                              activity.response_details.status.toLowerCase() === 'in_progress' ? 'warning' : 'default'
                                            }
                                            sx={{
                                              height: 20,
                                              '& .MuiChip-label': { px: 1, py: 0.5, fontSize: '0.7rem' },
                                              fontWeight: 500
                                            }}
                                          />
                                        )}
                                      </Box> */}
                                      </Box>

                                      {/* Activity Description - Show if available */}
                                      {activity.request_details?.description && (
                                        <Typography
                                          variant="body2"
                                          color="text.secondary"
                                          sx={{
                                            mb: 1.5,
                                            display: '-webkit-box',
                                            WebkitLineClamp: 2,
                                            WebkitBoxOrient: 'vertical',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis'
                                          }}
                                        >
                                          {activity.request_details.description}
                                        </Typography>
                                      )}

                                      {/* Activity Metadata */}
                                      <Box sx={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        gap: { xs: 1, sm: 2 },
                                        alignItems: 'center',
                                        mt: 1
                                      }}>
                                        <Box sx={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: 0.5,
                                          bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)',
                                          borderRadius: 1,
                                          px: 1,
                                          py: 0.5
                                        }}>
                                          <Schedule fontSize="small" color="action" />
                                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                            {formatDate(activity.created_at)}
                                          </Typography>
                                        </Box>

                                        {activity.user_id && (
                                          <Box sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.5,
                                            bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)',
                                            borderRadius: 1,
                                            px: 1,
                                            py: 0.5
                                          }}>
                                            <Info fontSize="small" color="action" />
                                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                              ID: {String(activity.user_id).substring(0, 8)}...
                                            </Typography>
                                          </Box>
                                        )}
                                      </Box>
                                    </Box>

                                    {/* Right side - Credits */}
                                    {activity.credits_used != null && (
                                      <Box sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        ml: 2,
                                        minWidth: 80,
                                        bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
                                        borderRadius: 2,
                                        p: 1,
                                        border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'}`,
                                      }}>
                                        {/* <BarChart color="primary" /> */}
                                        <Typography variant="h6" color="primary.main" sx={{ fontWeight: 600, mt: 0.5 }}>
                                          {activity.credits_used}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                          credits
                                        </Typography>
                                      </Box>
                                    )}
                                  </Box>
                                </Grid>
                              </Grid>
                            </CardContent>
                          </Card>
                        </Grid>
                      );
                    })}
                  </Grid>
                  {activitiesMeta && activitiesMeta.pages > 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                      <Pagination
                        count={activitiesMeta.pages}
                        page={activitiesPage}
                        onChange={handleActivitiesPageChange}
                        color="primary"
                      />
                    </Box>
                  )}
                </>
              ) : (
                <Paper
                  sx={{
                    p: 4,
                    textAlign: 'center',
                    borderRadius: 3,
                    bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
                    border: `1px dashed ${theme.palette.divider}`
                  }}
                >
                  <Box sx={{ mb: 2 }}>
                    <HistoryIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
                  </Box>
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No recent activities found
                  </Typography>
                  <Typography variant="body2" color="text.disabled" sx={{ maxWidth: 400, mx: 'auto', mb: 2 }}>
                    Security activities will appear here as you use the system. Start by running a scan or analysis.
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<Security />}
                    size="small"
                    onClick={onClose}
                  >
                    Run a Security Scan
                  </Button>
                </Paper>
              )}
            </TabPanel>

            <TabPanel value={tabValue} index={2}>
              {/* Usage Stats Tab */}
              <Typography variant="h6" gutterBottom>
                Usage Statistics
              </Typography>
              {trialSummary ? (
                <Grid container spacing={3}>

                  <Grid item xs={12}>
                    {/* Apps Used and Last Activity */}
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Card variant="outlined">
                          <CardContent>
                            <Typography variant="subtitle2" gutterBottom>
                              Apps Used
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                              {trialSummary.apps_used.map((app) => (
                                <Chip key={app} label={app} variant="outlined" size="small" />
                              ))}
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Card variant="outlined">
                          <CardContent>
                            <Typography variant="subtitle2" gutterBottom>
                              Last Activity
                            </Typography>
                            <Typography variant="body2">
                              {trialSummary.last_activity
                                ? formatDate(trialSummary.last_activity)
                                : 'No recent activity'
                              }
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>
                  </Grid>
                  {/* Enhanced Activities Section */}
                  <Grid item xs={12}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Recent Activities with Details
                        </Typography>
                        {enhancedActivities.length > 0 ? (
                          <TableContainer
                            component={Paper}
                            sx={{
                              maxHeight: 300,
                              borderRadius: '8px',
                              border: theme => `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)'}`,
                              boxShadow: 'none',
                              overflow: 'hidden',
                              '&::-webkit-scrollbar': {
                                width: '8px',
                                height: '8px',
                              },
                              '&::-webkit-scrollbar-track': {
                                background: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
                              },
                              '&::-webkit-scrollbar-thumb': {
                                background: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
                                borderRadius: '4px',
                              },
                              '&::-webkit-scrollbar-thumb:hover': {
                                background: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)',
                              },
                            }}
                          >
                            <Table stickyHeader size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell
                                    sx={{
                                      fontWeight: 600,
                                      backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(17, 17, 17, 0.9)' : 'rgba(248, 250, 252, 0.9)',
                                      color: theme => theme.palette.mode === 'dark' ? '#F1F5F9' : '#1E2533',
                                      borderBottom: theme => `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)'}`,
                                      whiteSpace: 'nowrap',
                                      py: 1.5
                                    }}
                                  >
                                    Type
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      fontWeight: 600,
                                      backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(17, 17, 17, 0.9)' : 'rgba(248, 250, 252, 0.9)',
                                      color: theme => theme.palette.mode === 'dark' ? '#F1F5F9' : '#1E2533',
                                      borderBottom: theme => `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)'}`,
                                      whiteSpace: 'nowrap',
                                      py: 1.5
                                    }}
                                  >
                                    Scan Type
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      fontWeight: 600,
                                      backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(17, 17, 17, 0.9)' : 'rgba(248, 250, 252, 0.9)',
                                      color: theme => theme.palette.mode === 'dark' ? '#F1F5F9' : '#1E2533',
                                      borderBottom: theme => `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)'}`,
                                      whiteSpace: 'nowrap',
                                      py: 1.5
                                    }}
                                  >
                                    App
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      fontWeight: 600,
                                      backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(17, 17, 17, 0.9)' : 'rgba(248, 250, 252, 0.9)',
                                      color: theme => theme.palette.mode === 'dark' ? '#F1F5F9' : '#1E2533',
                                      borderBottom: theme => `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)'}`,
                                      whiteSpace: 'nowrap',
                                      py: 1.5
                                    }}
                                  >
                                    Credits
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      fontWeight: 600,
                                      backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(17, 17, 17, 0.9)' : 'rgba(248, 250, 252, 0.9)',
                                      color: theme => theme.palette.mode === 'dark' ? '#F1F5F9' : '#1E2533',
                                      borderBottom: theme => `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)'}`,
                                      whiteSpace: 'nowrap',
                                      py: 1.5
                                    }}
                                  >
                                    Date
                                  </TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {enhancedActivities.map((activity, index) => (
                                  <TableRow
                                    key={activity.id}
                                    sx={{
                                      '&:nth-of-type(odd)': {
                                        backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
                                      },
                                      '&:hover': {
                                        backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
                                      },
                                      transition: 'background-color 0.2s',
                                    }}
                                  >
                                    <TableCell
                                      sx={{
                                        borderBottom: index === enhancedActivities.length - 1 ? 'none' :
                                          theme => `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'}`,
                                        py: 1
                                      }}
                                    >
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        {getActivityIcon(activity.activity_type)}
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                          {activity.activity_type.charAt(0).toUpperCase() + activity.activity_type.slice(1)}
                                        </Typography>
                                      </Box>
                                    </TableCell>
                                    <TableCell
                                      sx={{
                                        borderBottom: index === enhancedActivities.length - 1 ? 'none' :
                                          theme => `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'}`,
                                        py: 1
                                      }}
                                    >
                                      {activity.request_details?.scan_type?.replace(/_/g, ' ').toUpperCase() || '-'}
                                    </TableCell>
                                    <TableCell
                                      sx={{
                                        borderBottom: index === enhancedActivities.length - 1 ? 'none' :
                                          theme => `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'}`,
                                        py: 1
                                      }}
                                    >
                                      {activity.app_type}
                                    </TableCell>
                                    {/* <TableCell
                                      sx={{
                                        borderBottom: index === enhancedActivities.length - 1 ? 'none' :
                                          theme => `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'}`,
                                        py: 1
                                      }}
                                    >
                                      <Chip
                                        label={activity.trial_status}
                                        size="small"
                                        color={getTrialStatusColor(activity.trial_status) as any}
                                        sx={{
                                          height: '22px',
                                          '& .MuiChip-label': {
                                            px: 1,
                                            fontSize: '0.75rem',
                                            fontWeight: 500
                                          }
                                        }}
                                      />
                                    </TableCell> */}
                                    <TableCell
                                      sx={{
                                        borderBottom: index === enhancedActivities.length - 1 ? 'none' :
                                          theme => `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'}`,
                                        py: 1,
                                        fontWeight: 500,
                                        color: 'primary.main'
                                      }}
                                    >
                                      {activity.credits_used || 0}
                                    </TableCell>
                                    <TableCell
                                      sx={{
                                        borderBottom: index === enhancedActivities.length - 1 ? 'none' :
                                          theme => `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'}`,
                                        py: 1,
                                        whiteSpace: 'nowrap'
                                      }}
                                    >
                                      {formatDate(activity.created_at)}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                            No enhanced activities found
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                  No usage statistics available
                </Typography>
              )}
            </TabPanel>

            <TabPanel value={tabValue} index={3}>
              {/* Usage Trends Tab - New tab for usage summary data */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Usage Trends
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2">Period:</Typography>
                  <Button
                    variant={usageDays === 7 ? "contained" : "outlined"}
                    size="small"
                    onClick={() => setUsageDays(7)}
                  >
                    7 Days
                  </Button>
                  <Button
                    variant={usageDays === 30 ? "contained" : "outlined"}
                    size="small"
                    onClick={() => setUsageDays(30)}
                  >
                    30 Days
                  </Button>
                </Box>
              </Box>

              {usageSummary ? (
                <Grid container spacing={3}>
                  {/* Period Summary */}
                  <Grid item xs={12}>
                    <Card>
                      <CardContent>
                        <Typography variant="subtitle1" gutterBottom>
                          Period Summary: {new Date(usageSummary.period.start_date).toLocaleDateString()} - {new Date(usageSummary.period.end_date).toLocaleDateString()}
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={6} sm={3}>
                            <Card variant="outlined">
                              <CardContent sx={{ textAlign: 'center', py: 1 }}>
                                <Typography variant="h5" color="primary">
                                  {usageSummary.totals.activities}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  Activities
                                </Typography>
                              </CardContent>
                            </Card>
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <Card variant="outlined">
                              <CardContent sx={{ textAlign: 'center', py: 1 }}>
                                <Typography variant="h5" color="primary">
                                  {usageSummary.totals.scans}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  Scans
                                </Typography>
                              </CardContent>
                            </Card>
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <Card variant="outlined">
                              <CardContent sx={{ textAlign: 'center', py: 1 }}>
                                <Typography variant="h5" color="primary">
                                  {usageSummary.totals.fixes}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  Fixes
                                </Typography>
                              </CardContent>
                            </Card>
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <Card variant="outlined">
                              <CardContent sx={{ textAlign: 'center', py: 1 }}>
                                <Typography variant="h5" color="primary">
                                  {usageSummary.totals.credits_used}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  Credits Used
                                </Typography>
                              </CardContent>
                            </Card>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Daily Breakdown */}
                  <Grid item xs={12} md={12}>
                    <Card sx={{ height: '100%' }}>
                      <CardContent>
                        <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Timeline fontSize="small" />
                          Daily Activity
                        </Typography>

                        {/* Daily Activity Chart */}
                        <Box sx={{ height: 300, mb: 3 }}>
                          <ResponsiveContainer width="100%" height="100%">
                            <AreaChart
                              data={usageSummary.daily_breakdown}
                              margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                            >
                              <CartesianGrid strokeDasharray="3 3" opacity={0.1} />
                              <XAxis
                                dataKey="date"
                                tickFormatter={(date) => new Date(date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                                tick={{ fontSize: 12 }}
                              />
                              <YAxis tick={{ fontSize: 12 }} />
                              <RechartsTooltip
                                formatter={(value, name) => {
                                  const formattedName = name === 'activities' ? 'Activities' :
                                    name === 'scans' ? 'Scans' :
                                      name === 'fixes' ? 'Fixes' :
                                        name === 'credits_used' ? 'Credits' : name;
                                  return [value, formattedName];
                                }}
                                labelFormatter={(label) => new Date(label).toLocaleDateString()}
                                contentStyle={{
                                  backgroundColor: theme.palette.mode === 'dark' ? '#333' : '#fff',
                                  border: `1px solid ${theme.palette.divider}`,
                                  borderRadius: '8px',
                                  padding: '8px',
                                  fontSize: '12px'
                                }}
                              />
                              <Legend
                                verticalAlign="top"
                                height={36}
                                formatter={(value) => {
                                  return value === 'activities' ? 'Activities' :
                                    value === 'scans' ? 'Scans' :
                                      value === 'fixes' ? 'Fixes' :
                                        value === 'credits_used' ? 'Credits' : value;
                                }}
                              />
                              <Area
                                type="monotone"
                                dataKey="activities"
                                stackId="1"
                                stroke={theme.palette.primary.main}
                                fill={theme.palette.primary.main + '80'}
                                activeDot={{ r: 6 }}
                              />
                              <Area
                                type="monotone"
                                dataKey="scans"
                                stackId="2"
                                stroke={theme.palette.success.main}
                                fill={theme.palette.success.main + '80'}
                                activeDot={{ r: 6 }}
                              />
                              <Area
                                type="monotone"
                                dataKey="fixes"
                                stackId="3"
                                stroke={theme.palette.warning.main}
                                fill={theme.palette.warning.main + '80'}
                                activeDot={{ r: 6 }}
                              />
                              <Area
                                type="monotone"
                                dataKey="credits_used"
                                stackId="4"
                                stroke={theme.palette.error.main}
                                fill={theme.palette.error.main + '80'}
                                activeDot={{ r: 6 }}
                              />
                            </AreaChart>
                          </ResponsiveContainer>
                        </Box>

                        {/* Daily Activity Table */}
                        <Typography variant="subtitle2" gutterBottom>
                          Detailed Daily Breakdown
                        </Typography>
                        <TableContainer
                          sx={{
                            maxHeight: 240,
                            borderRadius: '8px',
                            border: theme => `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)'}`,
                            boxShadow: 'none',
                            overflow: 'hidden',
                            '&::-webkit-scrollbar': {
                              width: '8px',
                              height: '8px',
                            },
                            '&::-webkit-scrollbar-track': {
                              background: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
                            },
                            '&::-webkit-scrollbar-thumb': {
                              background: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
                              borderRadius: '4px',
                            },
                            '&::-webkit-scrollbar-thumb:hover': {
                              background: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)',
                            },
                          }}
                        >
                          <Table size="small" stickyHeader>
                            <TableHead>
                              <TableRow>
                                <TableCell
                                  sx={{
                                    fontWeight: 600,
                                    backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(17, 17, 17, 0.9)' : 'rgba(248, 250, 252, 0.9)',
                                    color: theme => theme.palette.mode === 'dark' ? '#F1F5F9' : '#1E2533',
                                    borderBottom: theme => `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)'}`,
                                    py: 1.5
                                  }}
                                >
                                  Date
                                </TableCell>
                                <TableCell
                                  align="right"
                                  sx={{
                                    fontWeight: 600,
                                    backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(17, 17, 17, 0.9)' : 'rgba(248, 250, 252, 0.9)',
                                    color: theme => theme.palette.mode === 'dark' ? '#F1F5F9' : '#1E2533',
                                    borderBottom: theme => `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)'}`,
                                    py: 1.5
                                  }}
                                >
                                  Activities
                                </TableCell>
                                <TableCell
                                  align="right"
                                  sx={{
                                    fontWeight: 600,
                                    backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(17, 17, 17, 0.9)' : 'rgba(248, 250, 252, 0.9)',
                                    color: theme => theme.palette.mode === 'dark' ? '#F1F5F9' : '#1E2533',
                                    borderBottom: theme => `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)'}`,
                                    py: 1.5
                                  }}
                                >
                                  Scans
                                </TableCell>
                                <TableCell
                                  align="right"
                                  sx={{
                                    fontWeight: 600,
                                    backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(17, 17, 17, 0.9)' : 'rgba(248, 250, 252, 0.9)',
                                    color: theme => theme.palette.mode === 'dark' ? '#F1F5F9' : '#1E2533',
                                    borderBottom: theme => `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)'}`,
                                    py: 1.5
                                  }}
                                >
                                  Fixes
                                </TableCell>
                                <TableCell
                                  align="right"
                                  sx={{
                                    fontWeight: 600,
                                    backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(17, 17, 17, 0.9)' : 'rgba(248, 250, 252, 0.9)',
                                    color: theme => theme.palette.mode === 'dark' ? '#F1F5F9' : '#1E2533',
                                    borderBottom: theme => `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)'}`,
                                    py: 1.5
                                  }}
                                >
                                  Credits
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {usageSummary.daily_breakdown.map((day, index, array) => (
                                <TableRow
                                  key={day.date}
                                  sx={{
                                    '&:nth-of-type(odd)': {
                                      backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
                                    },
                                    '&:hover': {
                                      backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
                                    },
                                    transition: 'background-color 0.2s',
                                  }}
                                >
                                  <TableCell
                                    sx={{
                                      borderBottom: index === array.length - 1 ? 'none' :
                                        theme => `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'}`,
                                      py: 1,
                                      fontWeight: 500
                                    }}
                                  >
                                    {new Date(day.date).toLocaleDateString()}
                                  </TableCell>
                                  <TableCell
                                    align="right"
                                    sx={{
                                      borderBottom: index === array.length - 1 ? 'none' :
                                        theme => `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'}`,
                                      py: 1
                                    }}
                                  >
                                    {day.activities}
                                  </TableCell>
                                  <TableCell
                                    align="right"
                                    sx={{
                                      borderBottom: index === array.length - 1 ? 'none' :
                                        theme => `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'}`,
                                      py: 1
                                    }}
                                  >
                                    {day.scans}
                                  </TableCell>
                                  <TableCell
                                    align="right"
                                    sx={{
                                      borderBottom: index === array.length - 1 ? 'none' :
                                        theme => `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'}`,
                                      py: 1
                                    }}
                                  >
                                    {day.fixes}
                                  </TableCell>
                                  <TableCell
                                    align="right"
                                    sx={{
                                      borderBottom: index === array.length - 1 ? 'none' :
                                        theme => `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'}`,
                                      py: 1,
                                      fontWeight: 500,
                                      color: 'primary.main'
                                    }}
                                  >
                                    {day.credits_used}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              )}
            </TabPanel>

          </>
        )}
      </DialogContent>
    </StyledDialog>
  );
};

export default TrialStatsDialog